// libraries
import React, { useId } from 'react'
import clsx from 'clsx'
import { useFormContext, RegisterOptions } from 'react-hook-form'

// utils
import { isNonWhitespaceString, useAnnounceOwnError } from '/lib/reactutils/utils'

// svg
import { Warning, Check } from '/react4xp/components/Svg/Ux/Ux'

interface CheckboxProps {
    id?: string
    name: string
    label?: string
    required?: boolean
    disabled?: boolean
    checked?: boolean
    children?: string
    className?: string
    errorMessages?: {
        required?: string
    }
    helperText?: string
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
}

export default function Checkbox({
    id,
    name,
    label,
    required,
    disabled,
    checked,
    children,
    errorMessages,
    className,
    helperText,
    onChange = () => {}
}: CheckboxProps) {

    const formContext = useFormContext()
    const register = formContext ? formContext.register : () => ({})
    const errors = formContext ? formContext.formState.errors : {}

    let validations: RegisterOptions = {
        onChange: (e) => onChange(e),
        required
    }

    if (required) {
        validations.required = errorMessages?.required
    }

    const submitCount = formContext ? formContext.formState.submitCount : 0

    const errorMessage = errors && errors[name]?.message
    const announceError = useAnnounceOwnError(Boolean(errorMessage), submitCount)

    const showHelperText = isNonWhitespaceString(helperText)
    const fieldId = id || name.replace(/\s+/g, '-')
    const instanceId = useId()
    const helperTextId = `${instanceId}-helper-text`
    const errorMessageId = `${instanceId}-error-message`

    const fieldLabelId = `${instanceId}-field-label`

    const describedBy = [
        label && fieldLabelId,
        showHelperText && helperTextId,
        errorMessage && errorMessageId
    ].filter(Boolean).join(' ') || undefined

    return (
        <div className={clsx(
            'form-line',
            className,
            errors && errors[name] && 'form-line--error'
        )}>

            {label && (
                <p id={fieldLabelId} className='text-md label-checkbox'>
                    {label}
                </p>
            )}

            <label className={clsx('form-line__checkbox-wrapper')} htmlFor={fieldId}>

                <input
                    id={fieldId}
                    type='checkbox'
                    defaultChecked={checked}
                    disabled={disabled || false}
                    onChange={onChange}
                    {...register(name, validations)}
                    aria-required={required || undefined}
                    aria-invalid={Boolean(errorMessage)}
                    aria-describedby={describedBy}
                />

                <span className='form-line__checkbox-wrapper__checkbox'>
                    <Check />
                </span>

                <span className='form-line__checkbox-wrapper__text'>

                    <span
                        className='text-md checkbox-text'
                        dangerouslySetInnerHTML={{ __html: children }}
                        suppressHydrationWarning
                    />

                </span>

            </label>

            {showHelperText && (
                <div
                    id={helperTextId}
                    className='form-line__help-text form-line__help-text--checkbox text-sm'
                    dangerouslySetInnerHTML={{ __html: helperText }}
                    suppressHydrationWarning
                />
            )}

            {errorMessage && (
                <p
                    id={errorMessageId}
                    role={announceError ? 'alert' : undefined}
                    className='
                        form-line__error-message
                        form-line__error-message--checkbox
                        labels-sm-regular
                    '
                >
                    <Warning />
                    {typeof errorMessage === 'string' && errorMessage}
                </p>
            )}

        </div>
    )
}